import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, getDoc, query, where, orderBy, limit } from 'firebase/firestore';
import AdminLayout from './AdminLayout';

const PAGE_SIZE = 10;

const PosSessionsPage = () => {
  const { posId } = useParams();
  const isAllSessionsMode = !posId;
  const [user, setUser] = useState(null);
  const [pos, setPos] = useState(null);
  const [location, setLocation] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const [sessionSales, setSessionSales] = useState({});
  const [sessionPullMoney, setSessionPullMoney] = useState({});
  const [expandedSession, setExpandedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [posNames, setPosNames] = useState({}); // posId -> posName

  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadData(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    loadSessions(user.uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dateFilter, customStartDate, customEndDate, displayCount]);

  const getFilterRange = () => {
    const now = new Date();
    let start = null;
    let end = null;
    switch (dateFilter) {
      case 'today':
        start = new Date(now);
        start.setHours(0, 0, 0, 0);
        end = now;
        break;
      case '3days':
        start = new Date(now);
        start.setDate(start.getDate() - 3);
        end = now;
        break;
      case '7days':
        start = new Date(now);
        start.setDate(start.getDate() - 7);
        end = now;
        break;
      case '30days':
        start = new Date(now);
        start.setDate(start.getDate() - 30);
        end = now;
        break;
      case '45days':
        start = new Date(now);
        start.setDate(start.getDate() - 45);
        end = now;
        break;
      case 'custom':
        if (customStartDate) {
          start = new Date(customStartDate);
          start.setHours(0, 0, 0, 0);
        }
        if (customEndDate) {
          end = new Date(customEndDate);
          end.setHours(23, 59, 59, 999);
        }
        break;
      default:
        break;
    }
    return { start, end };
  };

  const loadSessions = async (uid) => {
    setLoading(true);
    try {
      const db = getFirestore();
      const { start, end } = getFilterRange();
      let constraints = [orderBy('startTime', 'desc')];

      if (!isAllSessionsMode) {
        constraints.unshift(where('posId', '==', posId));
      }

      if (start) {
        constraints.push(where('startTime', '>=', start));
      }
      if (end) {
        constraints.push(where('startTime', '<=', end));
      }

      // Limit to only fetch what's displayed — avoids reading ALL documents in the date range
      constraints.push(limit(displayCount));

      const q = query(collection(db, 'users', uid, 'posSessions'), ...constraints);
      const sessionsSnapshot = await getDocs(q);
      const sessionsList = sessionsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setSessions(sessionsList);

      // In all-sessions mode, fetch POS names for each session
      if (isAllSessionsMode) {
        const uniquePosIds = [...new Set(sessionsList.map(s => s.posId).filter(Boolean))];
        const namesMap = {};
        await Promise.all(
          uniquePosIds.map(async (pid) => {
            try {
              const posDoc = await getDoc(doc(db, 'users', uid, 'pos', pid));
              namesMap[pid] = posDoc.exists() ? (posDoc.data().posName || pid.slice(-6)) : `Deleted POS (${pid.slice(-6)})`;
            } catch (e) {
              namesMap[pid] = `POS ${pid.slice(-6)}`;
            }
          })
        );
        setPosNames(namesMap);
      }
    } catch (e) {
      console.error('Error loading POS sessions:', e);
    } finally {
      setLoading(false);
    }
  };

  const visibleSessions = sessions; // already limited by the server-side query
  const hasMore = sessions.length === displayCount; // got a full page → there might be more

  const loadData = async (uid) => {
    if (isAllSessionsMode) return; // No specific POS to load

    try {
      const db = getFirestore();

      // Load POS — if deleted, keep going so we can still show sessions
      const posDoc = await getDoc(doc(db, 'users', uid, 'pos', posId));
      if (posDoc.exists()) {
        const posData = { id: posDoc.id, ...posDoc.data() };
        setPos(posData);

        // Load location
        if (posData.locationId) {
          const locDoc = await getDoc(doc(db, 'users', uid, 'locations', posData.locationId));
          if (locDoc.exists()) {
            setLocation({ id: locDoc.id, ...locDoc.data() });
          }
        }
      } else {
        // POS deleted — still allow viewing its sessions
        setPos(null);
      }
    } catch (e) {
      console.error('Error loading POS data:', e);
    }
  };

  const loadSessionSales = async (sessionId) => {
    if (sessionSales[sessionId] && sessionPullMoney[sessionId]) {
      setExpandedSession(expandedSession === sessionId ? null : sessionId);
      return;
    }
    try {
      const db = getFirestore();
      const salesSnapshot = await getDocs(
        query(collection(db, 'users', user.uid, 'posSessions', sessionId, 'sales'), orderBy('createdAt', 'desc'))
      );
      const sales = salesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setSessionSales(prev => ({ ...prev, [sessionId]: sales }));

      const pullMoneySnapshot = await getDocs(
        query(collection(db, 'users', user.uid, 'posSessions', sessionId, 'pullMoney'), orderBy('createdAt', 'desc'))
      );
      const pulls = pullMoneySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setSessionPullMoney(prev => ({ ...prev, [sessionId]: pulls }));

      setExpandedSession(sessionId);
    } catch (e) {
      console.error('Error loading session sales:', e);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatTime = (date) => {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getDuration = (start, end) => {
    if (!start || !end) return '—';
    const s = start.toDate ? start.toDate() : new Date(start);
    const e = end.toDate ? end.toDate() : new Date(end);
    const diffMs = e - s;
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffMins = Math.round((diffMs % 3600000) / 60000);
    return diffHrs > 0 ? `${diffHrs}h ${diffMins}m` : `${diffMins}m`;
  };

  // Totals based on filtered sessions
  const totalSessions = sessions.length;
  const totalSalesAll = sessions.reduce((sum, s) => sum + (s.totalSales || 0), 0);
  const totalTransactionsAll = sessions.reduce((sum, s) => sum + (s.totalTransactions || 0), 0);
  const totalCashSales = sessions.reduce((sum, s) => sum + (s.cash || 0), 0);
  const totalCardSales = sessions.reduce((sum, s) => sum + (s.cardSales || 0), 0);
  const totalStripeSales = sessions.reduce((sum, s) => sum + (s.stripeSales || 0), 0);

  const filterButtons = [
    { key: 'today', label: 'Today' },
    { key: '3days', label: '3 Days' },
    { key: '7days', label: '7 Days' },
    { key: '30days', label: '30 Days' },
    { key: '45days', label: '45 Days' },
    { key: 'custom', label: 'Custom' },
  ];

  if (loading) {
    return (
      <AdminLayout title="POS Sessions">
        <div className="text-center py-5">
          <img src="/fodyfee_shining.gif" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="POS Sessions">
      <div className="pos-sessions-page">
        {/* Header */}
        <div className="row mb-4 align-items-center">
          <div className={isAllSessionsMode ? 'col-md-12' : 'col-md-8'}>
            <button className="btn btn-link text-muted p-0 mb-2" onClick={() => navigate('/admin/pos')}>
              <i className="fas fa-arrow-left mr-2"></i>Back to POS
            </button>
            {isAllSessionsMode ? (
              <>
                <h4 className="font-weight-bold m-0">All POS Sessions</h4>
                <p className="text-muted mb-0">
                  <i className="fas fa-history mr-1"></i>
                  Sessions across all point-of-sale devices
                </p>
              </>
            ) : (
              <>
                <h4 className="font-weight-bold m-0">{pos?.posName || 'Deleted POS'}</h4>
                <p className="text-muted mb-0">
                  <i className="fas fa-store mr-1"></i>
                  {location?.companyName || location?.locationName || 'Unknown Location'}
                </p>
              </>
            )}
          </div>
          {!isAllSessionsMode && pos && (
            <div className="col-md-4 text-md-right mt-3 mt-md-0">
              <button
                className="btn btn-primary"
                style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
                onClick={() => navigate(`/admin/pos/${posId}`)}
              >
                <i className="fas fa-play mr-2"></i>Open POS
              </button>
            </div>
          )}
        </div>

        {/* Date Filters */}
        <div className="d-flex overflow-auto mb-2" style={{ gap: 8, paddingBottom: 4 }}>
          {filterButtons.map(btn => (
            <button
              key={btn.key}
              className={`btn btn-sm ${dateFilter === btn.key ? 'btn-primary' : 'btn-outline-secondary'}`}
              style={{
                borderRadius: 20,
                whiteSpace: 'nowrap',
                backgroundColor: dateFilter === btn.key ? '#ff6b35' : undefined,
                borderColor: dateFilter === btn.key ? '#ff6b35' : undefined,
              }}
              onClick={() => { setDateFilter(btn.key); setDisplayCount(PAGE_SIZE); }}
            >
              {btn.label}
            </button>
          ))}
        </div>
        {dateFilter === 'custom' && (
          <div className="d-flex align-items-end mb-4" style={{ gap: 12 }}>
            <div>
              <label className="small text-muted mb-1">Start Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={customStartDate}
                onChange={e => { setCustomStartDate(e.target.value); setDisplayCount(PAGE_SIZE); }}
              />
            </div>
            <div>
              <label className="small text-muted mb-1">End Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={customEndDate}
                onChange={e => { setCustomEndDate(e.target.value); setDisplayCount(PAGE_SIZE); }}
              />
            </div>
          </div>
        )}

        {/* KPI Cards */}
        <div className="row mb-4">
          {[
            { label: 'Sessions', value: totalSessions, icon: 'fa-history', color: '#ff6b35' },
            { label: 'Total Sales', value: formatCurrency(totalSalesAll), icon: 'fa-dollar-sign', color: '#28a745' },
            { label: 'Transactions', value: totalTransactionsAll, icon: 'fa-receipt', color: '#17a2b8' },
            { label: 'Cash', value: formatCurrency(totalCashSales), icon: 'fa-money-bill-wave', color: '#28a745' },
            { label: 'Card', value: formatCurrency(totalCardSales), icon: 'fa-credit-card', color: '#007bff' },
            { label: 'Stripe', value: formatCurrency(totalStripeSales), icon: 'fa-mobile-alt', color: '#6f42c1' },
          ].map((kpi, idx) => (
            <div key={idx} className="col-md-2 col-4 mb-3">
              <div className="card h-100" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div className="card-body text-center p-2">
                  <div
                    className="d-flex align-items-center justify-content-center mx-auto mb-1"
                    style={{ width: 36, height: 36, borderRadius: 10, background: kpi.color + '15' }}
                  >
                    <i className={`fas ${kpi.icon}`} style={{ color: kpi.color, fontSize: 14 }}></i>
                  </div>
                  <h6 className="font-weight-bold m-0" style={{ fontSize: '0.9rem' }}>{kpi.value}</h6>
                  <small className="text-muted" style={{ fontSize: '0.7rem' }}>{kpi.label}</small>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Sessions List */}
        <h5 className="font-weight-bold mb-3">
          Session History <span className="text-muted font-weight-normal" style={{ fontSize: '0.85rem' }}>({sessions.length})</span>
        </h5>
        {visibleSessions.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-history fa-3x text-muted mb-3"></i>
            <p className="text-muted">No sessions found for this period</p>
          </div>
        ) : (
          <div className="row">
            {visibleSessions.map((session) => {
              const isOpen = session.status === 'open';
              const isExpanded = expandedSession === session.id;
              const salesList = sessionSales[session.id] || [];
              const expectedCash = (session.startingCash || 0) + (session.cash || 0);
              return (
                <div key={session.id} className="col-12 mb-3">
                  <div className="card" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    {/* Session Header */}
                    <div className="card-body">
                      <div className="row align-items-center">
                        <div className="col-lg-2 col-md-3 col-6 mb-2 mb-lg-0">
                          <div className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                            {formatDate(session.startTime)}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {isOpen ? 'In progress' : getDuration(session.startTime, session.endTime)}
                          </div>
                        </div>
                        <div className="col-lg-1 col-md-2 col-6 mb-2 mb-lg-0 text-md-center">
                          <span className={`badge badge-${isOpen ? 'success' : 'secondary'}`} style={{ fontSize: '0.8rem' }}>
                            {isOpen ? 'Open' : 'Closed'}
                          </span>
                        </div>
                        <div className="col-lg-2 col-md-3 col-4 text-md-center">
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>Starting Cash</div>
                          <div className="font-weight-bold">{formatCurrency(session.startingCash)}</div>
                        </div>
                        <div className="col-lg-2 col-md-2 col-4 text-md-center">
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>Sales</div>
                          <div className="font-weight-bold" style={{ color: '#28a745' }}>{formatCurrency(session.totalSales)}</div>
                        </div>
                        <div className="col-lg-2 col-md-2 col-4 text-md-center">
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>Transactions</div>
                          <div className="font-weight-bold">{session.totalTransactions || 0}</div>
                        </div>
                        <div className="col-lg-3 col-12 text-lg-right">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            style={{ borderRadius: 10 }}
                            onClick={() => loadSessionSales(session.id)}
                          >
                            <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} mr-1`}></i>
                            {isExpanded ? 'Hide' : 'Details'}
                          </button>
                        </div>
                      </div>

                      {/* POS name badge in all-sessions mode */}
                      {isAllSessionsMode && session.posId && (
                        <div className="mt-2">
                          <button
                            className="badge badge-light border text-left"
                            style={{ fontSize: '0.8rem', cursor: 'pointer', background: '#f8f9fa' }}
                            onClick={() => navigate(`/admin/pos-sessions/${session.posId}`)}
                            title="View sessions for this POS"
                          >
                            <i className="fas fa-cash-register mr-1 text-muted"></i>
                            {posNames[session.posId] || session.posId}
                          </button>
                        </div>
                      )}

                      {/* Cash & Payment Summary (always visible for closed) */}
                      {!isOpen && (
                        <div className="mt-3 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                          <div className="row text-center">
                            <div className="col-6 col-md-2">
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>Expected Cash</div>
                              <div className="font-weight-bold">{formatCurrency(session.expectedCash ?? expectedCash)}</div>
                            </div>
                            <div className="col-6 col-md-2">
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>Actual Cash</div>
                              <div className="font-weight-bold">{formatCurrency(session.endingCash)}</div>
                            </div>
                            <div className="col-6 col-md-2">
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>Difference</div>
                              <div className={`font-weight-bold ${(session.cashDifference || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                                {formatCurrency(session.cashDifference)}
                              </div>
                            </div>
                            <div className="col-6 col-md-2">
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}><i className="fas fa-money-bill-wave text-success mr-1"></i>Cash</div>
                              <div className="font-weight-bold">{formatCurrency(session.cash)}</div>
                            </div>
                            <div className="col-6 col-md-2">
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}><i className="fas fa-credit-card text-primary mr-1"></i>Card</div>
                              <div className="font-weight-bold">{formatCurrency(session.cardSales)}</div>
                            </div>
                            <div className="col-6 col-md-2">
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}><i className="fas fa-mobile-alt text-info mr-1"></i>Stripe</div>
                              <div className="font-weight-bold">{formatCurrency(session.stripeSales)}</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Expanded Sales Details */}
                      {isExpanded && (
                        <div className="mt-3 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                          <h6 className="font-weight-bold mb-2">Session Sales</h6>
                          {salesList.length === 0 ? (
                            <p className="text-muted small">No sales in this session</p>
                          ) : (
                            <div className="table-responsive">
                              <table className="table table-sm table-hover" style={{ fontSize: '0.85rem' }}>
                                <thead>
                                  <tr>
                                    <th>Time</th>
                                    <th>Order #</th>
                                    <th>Customer</th>
                                    <th>Items</th>
                                    <th>Payment</th>
                                    <th className="text-right">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {salesList.map(sale => (
                                    <tr key={sale.id}>
                                      <td>{formatTime(sale.createdAt)}</td>
                                      <td><span className="font-weight-bold">#{sale.confirmationNumber || sale.id?.slice(-6)}{sale.guestNumber ? ` · Guest #${sale.guestNumber}` : ''}</span></td>
                                      <td>{sale.customerName || 'Walk-in'}</td>
                                      <td>{sale.itemsQty || sale.items?.length || 0}</td>
                                      <td>
                                        <span className="badge badge-light border">
                                          <i className={`fas mr-1 ${
                                            sale.paymentMethod === 'cash' ? 'fa-money-bill-wave text-success' :
                                            sale.paymentMethod === 'card' ? 'fa-credit-card text-primary' :
                                            'fa-mobile-alt text-info'
                                          }`}></i>
                                          {sale.paymentMethod}
                                        </span>
                                      </td>
                                      <td className="text-right font-weight-bold" style={{ color: '#ff6b35' }}>
                                        {formatCurrency(sale.total)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Pull Money Details */}
                          {isExpanded && (
                            <div className="mt-3 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                              <h6 className="font-weight-bold mb-2">Cash Pulled</h6>
                              {(sessionPullMoney[session.id] || []).length === 0 ? (
                                <p className="text-muted small">No cash pulled in this session</p>
                              ) : (
                                <div className="table-responsive">
                                  <table className="table table-sm table-hover" style={{ fontSize: '0.85rem' }}>
                                    <thead>
                                      <tr>
                                        <th>Time</th>
                                        <th>Employee</th>
                                        <th>Amount</th>
                                        <th>Notes</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(sessionPullMoney[session.id] || []).map(pull => (
                                        <tr key={pull.id}>
                                          <td>{formatTime(pull.createdAt)}</td>
                                          <td>{pull.employeeName || '—'}</td>
                                          <td className="text-right font-weight-bold" style={{ color: '#dc3545' }}>
                                            {formatCurrency(pull.amount)}
                                          </td>
                                          <td>{pull.notes || '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Cash Breakdown for closed sessions */}
                          {!isOpen && session.endingCashBreakdown && (
                            <div className="mt-3">
                              <h6 className="font-weight-bold mb-2">Cash Count Breakdown</h6>
                              <div className="row">
                                <div className="col-md-6">
                                  <p className="text-muted small mb-1">Starting Cash</p>
                                  <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                                    {session.startingCashBreakdown && Object.entries(session.startingCashBreakdown).map(([denom, count]) => (
                                      count > 0 && (
                                        <span key={denom} className="badge badge-light border" style={{ fontSize: '0.8rem' }}>
                                          ${denom} × {count}
                                        </span>
                                      )
                                    ))}
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <p className="text-muted small mb-1">Ending Cash</p>
                                  <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                                    {Object.entries(session.endingCashBreakdown).map(([denom, count]) => (
                                      count > 0 && (
                                        <span key={denom} className="badge badge-light border" style={{ fontSize: '0.8rem' }}>
                                          ${denom} × {count}
                                        </span>
                                      )
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load More */}
        {hasMore && (
          <div className="text-center mt-3 mb-5">
            <button
              className="btn btn-outline-primary"
              style={{ borderRadius: 20, minWidth: 200 }}
              onClick={() => setDisplayCount(prev => prev + PAGE_SIZE)}
            >
              <i className="fas fa-chevron-down mr-2"></i>
              Load More ({sessions.length - displayCount} remaining)
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default PosSessionsPage;
